/**
 * @typedef Market
 * @property {string} code - The ISO code of the market.
 * @property {string} name - The name of the market.
 * @property {string} currency - The ISO code of the currency used in the market.
 * @property {string} currency_symbol - The symbol of the currency used in the market.
 * @property {string} flag - The URL of the flag of the market.
 */

/**
 * @typedef Suggestion
 * @property {object} parts - The parts of the suggestion.
 * @property {object} parts.country - The country part of the suggestion.
 * @property {string} parts.country.handle - The ISO code of the country.
 * @property {string} parts.country.name - The name of the country.
 */

if (!customElements.get('localization-banner')) {
  customElements.define(
    'localization-banner',
    class LocalizationBanner extends HTMLElement {
      constructor() {
        super();

        this.trap = window.traps.store['localization-tray'];

        /** @type {HTMLElement | null} */
        this.content = this.querySelector('[data-localization-content]');

        /** @type {HTMLScriptElement | null} */
        this.json = this.querySelector('[data-localization-json]');

        this.suggestion = null;
      }

      async connectedCallback() {
        if (localStorage.getItem('viewed-localization-banner') === 'true') {
          return;
        }

        const suggestion = await this.getSuggestedMarket();

        if (suggestion && this.json && this.content) {
          this.suggestion = suggestion;

          this.renderForm(suggestion, this.json.innerHTML);

          if (this.trap) {
            if (window.traps.store['cookies'].active) {
              return;
            }

            this.trap.activate();

            localStorage.setItem('viewed-localization-banner', 'true');
          }
        }
      }

      async getSuggestedMarket() {
        const response = await fetch(
          window.Shopify.routes.root +
            'browsing_context_suggestions.json' +
            '?country[enabled]=true' +
            `&country[exclude]=${window.Shopify.country}`,
        );

        if (!response.ok) {
          throw new Error(response.statusText);
        }

        const data = await response.json();

        if (response.ok) {
          const suggestions = data.suggestions;

          if (suggestions.length > 0) return suggestions[0];

          return null;
        }
      }

      /**
       * @param {Suggestion} suggestion - The market suggestions from the API.
       * @param {string} data - The JSON data from the snippet.
       */
      renderForm(suggestion, data) {
        const country = suggestion.parts?.country;
        const json = JSON.parse(data);

        if (country && this.content && json && json.markets) {
          const id = crypto.randomUUID();

          this.content.innerHTML = `
            <form id="${id}" action="/localization" method="POST" class="flow">
              <input type="hidden" name="_method" value="PUT">
              <label for="country-select-${id}">${json.content?.update_country}</label>
              <div class="select-input text-subtitle-md">
                <select name="country_code" id="country-select-${id}">
                  ${json.markets
                    ?.map((/** @type {Market} */ market) => {
                      return `
                      <option value="${market.code}" ${market.code === country.handle ? `selected` : ''} data-flag="${market.flag}">${market.name} / ${market.currency} ${market.currency_symbol}</option>
                    `;
                    })
                    .join('')}
                </select>
              </div>
              <button type="submit">${json.content?.submit}</button>
            </form>
          `;

          /** @type {HTMLSelectElement | null} */
          const select = this.content.querySelector('.select-input');

          if (select) {
            const current = json.markets.find((/** @type {Market} */ market) => market.code === country.handle);

            if (current) {
              select.style.setProperty('--country-flag', `url(${current.flag})`);
            }

            select.addEventListener('change', () => {
              /** @type {HTMLElement | null} */
              const selected = select.querySelector(':checked');

              if (selected) select.style.setProperty('--country-flag', `url(${selected.dataset.flag})`);
            });
          }
        }
      }
    },
  );
}
